import type { TableColumn } from 'uikit';
import { agGridAdapter, Button, IconCards, Popover, TableFilterContainer } from 'uikit';

import { useClickOutside } from '@consta/uikit/useClickOutside';
import type { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CheckPin } from './CheckPin';
import { getColumnId, getPinnedFromTableState } from './helpers';

import Styles from './TableSettings.module.css';

const NameCell = ({ value }: { value: string }) => <div className={Styles.nameCell}>{value}</div>;

NameCell.displayName = 'NameCell';

interface ITableSettingsProps {
    columns: (TableColumn<any> & {
        headerName?: string;
        field?: string;
        disabled?: boolean;
        lockPosition?: 'left' | 'right' | undefined;
        suppressMovable?: boolean;
        hiddenTitle?: string;
        pinned?: boolean;
    })[];
    onConfirm: (selected: Record<string, boolean>) => void;
    isColumnVisible: (title: string) => boolean;
    showPinColumn?: boolean;
    tableApi?: AgGridReact | undefined | null;
}

const getRowClass = (params: any) => [...(params.data?.disabled ? ['disabledRow'] : [])];

const styleOptions = agGridAdapter({
    size: 'm',
    borderBetweenColumns: true,
    borderBetweenRows: true,
});

const columnToFilterItem = ({
    accessor,
    title,
    hiddenTitle = null,
    headerName = null,
    disabled = false,
    colId = null,
}: TableColumn<any> & {
    hiddenTitle?: string | null;
    colId?: string | null;
    headerName?: string | null;
    disabled?: boolean;
}) => {
    const columnTitle = typeof title === 'string' ? title : accessor;
    const optionalTitle = typeof hiddenTitle === 'string' && hiddenTitle;
    const agGridTitle = typeof headerName === 'string' ? headerName : null;

    return {
        name: agGridTitle ?? (optionalTitle ? optionalTitle! : columnTitle!),
        value: agGridTitle ?? columnTitle!,
        colId,
        disabled,
    };
};

export const TableSettings = ({
    columns,
    onConfirm,
    isColumnVisible,
    showPinColumn,
    tableApi,
}: ITableSettingsProps) => {
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [pinnedColumns, setPinnedColumns] = useState<Array<string | undefined>>([]);
    const [selectionChanged, setSelectionChanged] = useState<number>();

    const gridRef = useRef<AgGridReact>(null);
    const filterContainerRef = useRef(null);
    const buttonRef = useRef(null);

    const applicableColumns = useMemo(
        () =>
            columns.filter(
                ({ title, accessor, headerName }) =>
                    typeof title === 'string' || typeof accessor === 'string' || typeof headerName === 'string',
            ),
        [columns],
    );

    useEffect(() => {
        if (isDialogVisible) {
            if (tableApi) {
                const columnState = tableApi?.api?.getColumnState?.();
                const pinnedTableState = columnState?.filter((column) => column.pinned);

                const pinnedFromTableState = getPinnedFromTableState({
                    tableStateWithOnlyPinned: pinnedTableState,
                    columns: applicableColumns as ColDef[],
                });

                setPinnedColumns(pinnedFromTableState);
            } else {
                const pinnedApplicableColumns = applicableColumns.filter((item) => !!item.pinned);
                const pinnedColumnNames = pinnedApplicableColumns.map((column) => column.headerName);

                setPinnedColumns(pinnedColumnNames);
            }
        }
    }, [applicableColumns, isDialogVisible, tableApi]);

    const items = useMemo(() => applicableColumns.map(columnToFilterItem), [applicableColumns]);

    const filterValue = useMemo(
        () => applicableColumns.filter((column) => isColumnVisible(getColumnId(column))).map(columnToFilterItem),
        [applicableColumns, isColumnVisible],
    );

    const handleOnConfirm = useCallback(() => {
        if (gridRef?.current) {
            const { api } = gridRef.current;

            const checkedNodes = api.getSelectedNodes().map((node) => ({
                ...node.data,
            }));

            const result = checkedNodes.reduce((accumulator, item) => {
                accumulator[item?.colId ?? item.name] = true;

                return accumulator;
            }, {});

            onConfirm({ ...result, pinnedColumNames: pinnedColumns });
            setIsDialogVisible(false);
        }
    }, [onConfirm, pinnedColumns]);

    const handleOnCancel = useCallback(() => {
        setIsDialogVisible(false);
    }, []);

    const handleTableSettings = useCallback(() => {
        setIsDialogVisible((isVisible) => !isVisible);
    }, [setIsDialogVisible]);

    const handleColumnPin = useCallback(
        (columnName: string) => {
            const isColumnPinned = pinnedColumns.includes(columnName);

            if (isColumnPinned) {
                setPinnedColumns((previousState) => previousState.filter((item) => item !== columnName));
            } else {
                setPinnedColumns((previousState) => [...previousState, columnName]);
            }
        },
        [pinnedColumns],
    );

    const columnDefsHavePin = useMemo<ColDef[]>(
        () => [
            {
                field: '',
                lockPosition: 'left',
                headerName: '',
                checkboxSelection: true,
                showDisabledCheckboxes: true,
                cellRenderer: CheckPin,
                cellRendererParams: {
                    currentPinnedState: pinnedColumns,
                    applicableColumns,
                    onClick: (params: string) => handleColumnPin(params),
                },
                width: 85,
                maxWidth: 85,
                suppressSizeToFit: true,
                sortable: false,
            },
            {
                field: 'name',
                headerName: 'Колонки',
                sortable: false,
                tooltipField: 'name',
                cellRenderer: NameCell,
            },
        ],
        [applicableColumns, handleColumnPin, pinnedColumns],
    );

    const columnDefault = useMemo<ColDef[]>(
        () => [
            {
                field: '',
                lockPosition: 'left',
                headerName: '',
                checkboxSelection: true,
                showDisabledCheckboxes: true,
                width: 52,
                maxWidth: 52,
                suppressSizeToFit: true,
            },
            { field: 'name', headerName: 'Колонки', sortable: false },
        ],
        [],
    );

    const handleSelectAllRows = useCallback(() => {
        if (gridRef?.current) {
            const { api } = gridRef.current;

            api?.selectAll();
        }
    }, []);

    const handleDeselectAllRows = useCallback(() => {
        if (gridRef?.current) {
            const { api } = gridRef.current;

            if (showPinColumn) {
                api?.selectAll();
                setPinnedColumns([]);

                return;
            }

            const selectedNodes = api.getSelectedNodes();

            selectedNodes.forEach((node) => {
                if (!node.data.disabled) {
                    node.setSelected(false);
                }
            });
        }
    }, [showPinColumn]);

    const defaultColDef = useMemo(
        () => ({
            flex: 1,
            resizable: true,
        }),
        [],
    );

    const setCheckboxes = useCallback(() => {
        if (Array.isArray(filterValue) && filterValue.length && gridRef.current) {
            gridRef.current.api?.forEachNode((node) => {
                node.setSelected(
                    (filterValue as any[]).some((selectedFilterItem) => R.equals(selectedFilterItem, node.data)),
                );
            });
        }
    }, [filterValue]);

    useEffect(() => {
        setCheckboxes();
    }, [filterValue, setCheckboxes]);

    const onFirstDataRendered = useCallback(() => {
        setCheckboxes();
    }, [setCheckboxes]);

    useClickOutside({
        isActive: isDialogVisible,
        ignoreClicksInsideRefs: [buttonRef, filterContainerRef],
        handler: handleTableSettings,
    });

    const onSelectionChanged = () => {
        if (gridRef?.current) {
            const { api } = gridRef.current;
            const selectedNodes = api?.getSelectedNodes();

            setSelectionChanged(selectedNodes?.length);
        }
    };

    const isDisabledDeselectButton = useMemo(
        () => !pinnedColumns.length && selectionChanged === items.length,
        [items.length, pinnedColumns.length, selectionChanged],
    );

    return (
        <>
            <Button
                onlyIcon
                iconLeft={IconCards}
                title="Настройки таблицы"
                size="s"
                view="clear"
                onClick={handleTableSettings}
                ref={buttonRef}
            />
            {isDialogVisible && (
                <Popover ref={filterContainerRef} anchorRef={buttonRef}>
                    <TableFilterContainer
                        className={Styles.tableFilterContainer}
                        onConfirm={handleOnConfirm}
                        onCancel={handleOnCancel}
                    >
                        <div className="TableTextFilter-Controls">
                            {!showPinColumn && (
                                <Button
                                    label="Выбрать все"
                                    onClick={handleSelectAllRows}
                                    className="Button Button_size_m Button_view_clear Button_width_default Button_form_brick MixFocus"
                                />
                            )}
                            <Button
                                label="Сбросить"
                                onClick={handleDeselectAllRows}
                                className="Button Button_size_m Button_view_clear Button_width_default Button_form_brick MixFocus"
                                disabled={isDisabledDeselectButton}
                            />
                        </div>
                        <div className={Styles.wrapperAgGrid}>
                            <AgGridReact
                                {...styleOptions}
                                overlayNoRowsTemplate="Нет данных"
                                onSelectionChanged={onSelectionChanged}
                                ref={gridRef}
                                rowData={items}
                                columnDefs={showPinColumn ? columnDefsHavePin : columnDefault}
                                defaultColDef={defaultColDef}
                                animateRows
                                rowSelection={{
                                    headerCheckbox: false,
                                    mode: 'multiRow',
                                    checkboxes: false,
                                }}
                                tooltipShowMode="whenTruncated"
                                getRowClass={getRowClass}
                                onFirstDataRendered={onFirstDataRendered}
                            />
                        </div>
                    </TableFilterContainer>
                </Popover>
            )}
        </>
    );
};

TableSettings.displayName = 'TableSettings';
